export type ReviewReasonSeverity = 'BLOCKER' | 'CONCERN' | 'INFO';

export type ReviewReasonCategory =
  | 'EMPLOYER'
  | 'INCOME'
  | 'LOCATION'
  | 'ROLE'
  | 'TENURE'
  | 'DIGITAL_PRESENCE';

export type ReviewReasonSourceCheck = {
  gate: string;
  step: string;
  status: string;
  isHardStop?: boolean;
};

export type ReviewReasonEvidenceTone =
  | 'declared'
  | 'observed'
  | 'employer'
  | 'role'
  | 'neutral'
  | 'workplace'
  | 'policy'
  /** Legitimacy gate — distinct from structural sanity palette */
  | 'places'
  | 'reviews'
  | 'vision'
  | 'satellite'
  | 'domain'
  | 'registry'
  | 'aiInsight'
  | 'threshold';

export type ReviewReasonEvidence = {
  label: string;
  value: string;
  tone?: ReviewReasonEvidenceTone;
};

export type ReviewReason = {
  id: string;
  severity: ReviewReasonSeverity;
  title: string;
  explanation: string;
  category: ReviewReasonCategory;
  sourceChecks: ReviewReasonSourceCheck[];
  recommendedAction?: string;
  evidence?: ReviewReasonEvidence[];
};

export type EmployerConflictInfo = {
  declaredEmployer: string | null;
  declaredJobTitle: string | null;
  declaredWorkArrangement: string | null;
  externalEmployer: string | null;
  externalSourceLabel: string;
  explanation: string;
};

export type EmployerReviewStatus =
  | 'verified'
  | 'needs_review'
  | 'not_confirmed'
  | 'in_progress'
  | 'not_started';

export type EmployerReviewSummaryViewModel = {
  status: EmployerReviewStatus;
  statusLabel: string;
  /** Blockers + concerns only — excludes INFO. */
  issueCount: number;
  blockerCount: number;
  concernCount: number;
  /** Top blocker/concern findings for the primary panel (max 3). */
  topReasons: ReviewReason[];
  /** Additional blocker/concern findings beyond the top 3. */
  additionalReasons: ReviewReason[];
  /** All blocker/concern findings — for the details/evidence panel. */
  evidenceFindings: ReviewReason[];
  recommendedAction: string | null;
  conflict: EmployerConflictInfo | null;
  cleanPassMessage: string | null;
};

/** e.g. "1 blocker · 1 concern" */
export function formatSeveritySummary(blockerCount: number, concernCount: number): string | null {
  const parts: string[] = [];
  if (blockerCount === 1) parts.push('1 blocker');
  else if (blockerCount > 1) parts.push(`${blockerCount} blockers`);
  if (concernCount === 1) parts.push('1 concern');
  else if (concernCount > 1) parts.push(`${concernCount} concerns`);
  return parts.length ? parts.join(' · ') : null;
}
